import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ArrowUpTrayIcon, DocumentIcon, TrashIcon } from "@heroicons/react/24/outline";
import fileRelationService from "../../../../services/fileRelationService";
import fileService from "../../../../services/fileService";
import vectorService from "../../../../services/vectorService";
import SasUrlService from "../../../../services/SasUrlService";
import { transformFileDetailsToDisplayData } from "../../../../helpers/dataTransform";
import { EnhancedUploadFileModal } from "../../../../components/ui/EnhancedUploadFileModal";
import { DeleteConfirmModal } from "../../../../components/ui/DeleteConfirmModal";
import { MAX_FACILITY_FILES } from "../../constants/facility.constants";

const ContextFilesTab = ({ facilityNumber }) => {
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openingFileId, setOpeningFileId] = useState(null);

  const loadFiles = async (bustCache = false) => {
    if (!facilityNumber) return;
    setIsLoading(true);
    try {
      const relations = await fileRelationService.getFileRelationsByFacility(facilityNumber, bustCache);
      const fileIds = (relations || []).map((r) => r.file_id).filter(Boolean);
      if (fileIds.length === 0) {
        setFiles([]);
        return;
      }
      const fileDetailsResponse = await fileService.getFacilityFileDetails(fileIds);
      setFiles(transformFileDetailsToDisplayData(fileDetailsResponse));
    } catch (error) {
      console.error("Error loading facility context files:", error);
      toast.error("Failed to load context files");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, [facilityNumber]);

  const handleUploadComplete = async () => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    await loadFiles(true);
  };

  const handleDelete = async (file) => {
    setIsDeleting(true);
    try {
      if (file.fileUrl) {
        await vectorService.softDeleteDocuments([{ file_url: file.fileUrl }]);
        await fileService.deleteFile(file.id);
      }
      toast.success("File deleted successfully");
      await loadFiles(true);
    } catch (error) {
      console.error("Error deleting facility file:", error);
      toast.error("Failed to delete file. Please try again.");
    } finally {
      setIsDeleting(false);
      setDeletingFile(null);
    }
  };

  const handleOpenFile = async (file) => {
    if (openingFileId) return;
    setOpeningFileId(file.id);
    try {
      const { public_url } = await SasUrlService.generateSasUrl(file.fileUrl);
      window.open(public_url, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error("Failed to open file. Please try again.");
    } finally {
      setOpeningFileId(null);
    }
  };

  const atFileLimit = files.length >= MAX_FACILITY_FILES;

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-gray-900">Relation context files</h3>
          <p className="text-sm text-gray-500">
            {files.length} of {MAX_FACILITY_FILES} files used &middot; grounding context for this facility
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowUploadModal(true)}
          disabled={atFileLimit}
          className="inline-flex items-center gap-2 rounded-xl bg-[#2B3674] px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ArrowUpTrayIcon className="h-4 w-4" />
          Upload files
        </button>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-gray-500">Loading files...</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#F6F4FE]">
              <tr className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">Date added</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No context files uploaded yet.
                  </td>
                </tr>
              ) : (
                files.map((file) => (
                  <tr key={file.id} className="border-t border-gray-100">
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => handleOpenFile(file)}
                        disabled={!!openingFileId}
                        className="flex items-center gap-2 font-medium text-[#2B3674] hover:underline disabled:opacity-50"
                      >
                        <DocumentIcon className="h-4 w-4" />
                        {openingFileId === file.id ? `${file.name}…` : file.name}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">{file.fileType}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{file.fileSize}</td>
                    <td className="px-4 py-3 text-gray-500">{file.dateCreated}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setDeletingFile(file)}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:text-red-700"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {!atFileLimit && (
            <button
              type="button"
              onClick={() => setShowUploadModal(true)}
              className="flex w-full flex-col items-center justify-center gap-1 border-t border-dashed border-gray-200 py-8 text-sm text-gray-500 hover:bg-gray-50"
            >
              <span className="font-semibold text-gray-600">Drop files here to add context</span>
              <span className="text-xs text-gray-400">PDF, MD, DOCX or TXT · up to 10 MB each</span>
            </button>
          )}
        </div>
      )}

      <EnhancedUploadFileModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={handleUploadComplete}
        facilityId={facilityNumber}
        maxFiles={MAX_FACILITY_FILES}
        currentFileCount={files.length}
        existingFileNames={files.map((f) => f.name)}
      />

      <DeleteConfirmModal
        open={!!deletingFile}
        onClose={() => setDeletingFile(null)}
        onDelete={() => handleDelete(deletingFile)}
        fileName={deletingFile?.name}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default ContextFilesTab;
